/**
 * The Good Patch
 * Custom storefront JavaScript
 *
 * 1. Normalizes Recharge delivery-frequency wording.
 * 2. Opens PDP galleries on media position #2.
 */

(() => {
  'use strict';

  /* =========================================================
     RECHARGE — NORMALIZE DELIVERY-FREQUENCY WORDING
     ========================================================= */

  const RECHARGE_WIDGET_SELECTOR = 'recharge-subscription-widget';

  const queryDeepAll = (root, selector, results = []) => {
    if (!root || typeof root.querySelectorAll !== 'function') {
      return results;
    }

    root.querySelectorAll(selector).forEach((element) => {
      results.push(element);
    });

    root.querySelectorAll('*').forEach((element) => {
      if (element.shadowRoot) {
        queryDeepAll(element.shadowRoot, selector, results);
      }
    });

    return results;
  };

  const parseMoney = (value) => {
    if (!value) return null;

    const cleaned = String(value)
      .replace(/[^0-9.,-]/g, '')
      .replace(/,(?=\d{2}$)/, '.')
      .replace(/,/g, '');

    const number = Number.parseFloat(cleaned);

    return Number.isFinite(number) ? number : null;
  };

  const calculateRechargeDiscount = (root) => {
    const originalElement = queryDeepAll(
      root,
      '[part~="rc-purchase-option__original-price"]'
    )[0];

    const discountedElement = queryDeepAll(
      root,
      '[part~="rc-purchase-option__discounted-price"]'
    )[0];

    const original = parseMoney(originalElement?.textContent);
    const discounted = parseMoney(discountedElement?.textContent);

    if (
      original === null ||
      discounted === null ||
      original <= 0 ||
      discounted >= original
    ) {
      return '';
    }

    const percentage = Math.round(
      ((original - discounted) / original) * 100
    );

    return `${percentage}% off`;
  };

  const normalizeRechargeFrequency = (
    text,
    fallbackDiscount
  ) => {
    const originalText = String(text || '').trim();

    const intervalMatch = originalText.match(
      /(?:deliver\s+every\s+)?(\d+)\s+(day|week|month|year)s?/i
    );

    if (!intervalMatch) {
      return originalText;
    }

    const amount = Number(intervalMatch[1]);
    const unit = intervalMatch[2].toLowerCase();

    const intervalText =
      amount === 1
        ? `Deliver every ${unit}`
        : `Deliver every ${amount} ${unit}s`;

    const displayedDiscount = originalText.match(
      /\d+(?:\.\d+)?%\s*(?:off)?/i
    );

    const discountText = displayedDiscount
      ? `${displayedDiscount[0]
          .replace(/\s*off$/i, '')
          .trim()} off`
      : fallbackDiscount;

    return discountText
      ? `${intervalText}, ${discountText}`
      : intervalText;
  };

  const updateRechargeWidget = (widget) => {
    if (!widget?.shadowRoot) return;

    const discount = calculateRechargeDiscount(
      widget.shadowRoot
    );

    const selects = queryDeepAll(
      widget.shadowRoot,
      'select[part~="rc-plans-select"], [part~="rc-plans-select"]'
    );

    selects.forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) return;

      Array.from(select.options).forEach((option) => {
        const updatedText = normalizeRechargeFrequency(
          option.textContent,
          discount
        );

        if (option.textContent !== updatedText) {
          option.textContent = updatedText;
        }
      });
    });
  };

  const refreshRechargeWidgets = () => {
    document
      .querySelectorAll(RECHARGE_WIDGET_SELECTOR)
      .forEach(updateRechargeWidget);
  };

  let rechargeRefreshTimer = null;

  const scheduleRechargeRefresh = () => {
    window.clearTimeout(rechargeRefreshTimer);

    rechargeRefreshTimer = window.setTimeout(() => {
      refreshRechargeWidgets();
    }, 100);
  };

  const rechargeObserver = new MutationObserver(
    scheduleRechargeRefresh
  );

  const initializeRecharge = () => {
    rechargeObserver.observe(document.documentElement, {
      childList: true,
      subtree: true
    });

    refreshRechargeWidgets();

    [250, 750, 1500, 3000].forEach((delay) => {
      window.setTimeout(refreshRechargeWidgets, delay);
    });

    document.addEventListener(
      'change',
      scheduleRechargeRefresh,
      true
    );

    if ('customElements' in window) {
      customElements
        .whenDefined(RECHARGE_WIDGET_SELECTOR)
        .then(scheduleRechargeRefresh)
        .catch(() => {});
    }
  };


  /* =========================================================
     PDP — OPEN GALLERY ON MEDIA POSITION #2
     Keeps media #1 as the collection-card image.
     ========================================================= */

  const PRODUCT_TEMPLATE_CLASS = 'template-product';
  const MAX_GALLERY_ATTEMPTS = 30;
  const GALLERY_RETRY_DELAY = 150;

  const isProductPage = () =>
    document.body.classList.contains(
      PRODUCT_TEMPLATE_CLASS
    );

  const getMainGalleryElement = () =>
    document.querySelector(
      '.template-product .wt-product__gallery [data-gallery]'
    ) ||
    document.querySelector(
      '.template-product .wt-product__gallery .swiper'
    );

  const getGallerySwiper = () => {
    const gallery = getMainGalleryElement();

    if (!gallery) return null;

    return (
      gallery.swiper ||
      gallery.closest('.swiper')?.swiper ||
      gallery.querySelector('.swiper')?.swiper ||
      null
    );
  };

  const getGalleryThumbnails = () =>
    Array.from(
      document.querySelectorAll(
        '.template-product ' +
          '.wt-product__gallery [data-thumbs] ' +
          '.wt-slider__slide, ' +
        '.template-product ' +
          '.wt-product__gallery [data-thumbs] ' +
          '.swiper-slide'
      )
    );

  const openSecondProductMedia = () => {
    if (!isProductPage()) return true;

    const swiper = getGallerySwiper();

    if (swiper?.slides?.length > 1) {
      /*
       * Position #2 has zero-based index 1.
       * Use slideToLoop when the theme enables loop mode.
       */
      if (
        swiper.params?.loop &&
        typeof swiper.slideToLoop === 'function'
      ) {
        swiper.slideToLoop(1, 0, false);
      } else if (typeof swiper.slideTo === 'function') {
        swiper.slideTo(1, 0, false);
      }

      return true;
    }

    const thumbnails = getGalleryThumbnails();

    if (thumbnails.length > 1) {
      const secondThumbnail = thumbnails[1];
      const interactiveElement =
        secondThumbnail.querySelector(
          'button, a, [role="button"]'
        );

      if (interactiveElement) {
        interactiveElement.click();
      } else {
        secondThumbnail.click();
      }

      return true;
    }

    return false;
  };

  let galleryInitializationTimer = null;

  const initializeSecondProductMedia = () => {
    if (!isProductPage()) return;

    window.clearInterval(galleryInitializationTimer);

    let attempts = 0;

    galleryInitializationTimer = window.setInterval(() => {
      attempts += 1;

      const completed = openSecondProductMedia();

      if (
        completed ||
        attempts >= MAX_GALLERY_ATTEMPTS
      ) {
        window.clearInterval(
          galleryInitializationTimer
        );
      }
    }, GALLERY_RETRY_DELAY);
  };


  /* =========================================================
     INITIALIZATION
     ========================================================= */

  const initializeCustomScripts = () => {
    initializeRecharge();
    initializeSecondProductMedia();
  };

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      initializeCustomScripts,
      { once: true }
    );
  } else {
    initializeCustomScripts();
  }

  /*
   * Reinitialize when Shopify reloads a section inside
   * the theme editor.
   */
  document.addEventListener(
    'shopify:section:load',
    () => {
      scheduleRechargeRefresh();
      initializeSecondProductMedia();
    }
  );

  document.addEventListener(
    'shopify:block:select',
    () => {
      scheduleRechargeRefresh();
    }
  );
})();